import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import BiodealLogo from "./BiodealLogo";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";

/**
 * Simplified primary navigation (Phase 1A):
 *   Logo = Home · Products ▾ · Vet Professionals · Training · Where to Buy · Partner · About
 * Utility: [Contact / WhatsApp] pill on the right.
 *
 * Fit-based breakpoint: `xl:` here is remapped in tailwind.config.js so that
 * primary nav appears at the smallest width where labels do not wrap. Below
 * that width the hamburger drawer is used. See tailwind.config.js.
 *
 * Note on legacy nav items (per approval): Solutions / Stockists / Distribution
 * remain reachable via existing routes and via the Partner dropdown. Where-to-Buy
 * and the merged /partner landing arrive in Phase 1B; for now these primary
 * items link to the safest existing destination.
 */
const NAV = [
  {
    label: "Products",
    to: "/products",
    testid: "nav-products",
    submenu: [
      { label: "All Medicines", to: "/products", testid: "nav-products-all" },
      { label: "By Species", to: "/products#species", testid: "nav-products-species" },
      { label: "By Health Need", to: "/solutions", testid: "nav-products-need" },
      { label: "Veterinary Instruments", to: "/instruments", testid: "nav-instruments" },
      { label: "Cold Chain & Vaccine Support", to: "/cold-chain", testid: "nav-cold-chain" },
      { label: "Companion Animals", to: "/products?species=dogs-cats", testid: "nav-companion" },
    ],
  },
  { label: "Vet Professionals", to: "/vet-professionals", testid: "nav-vet-professionals" },
  { label: "Training", to: "/training", testid: "nav-training" },
  {
    label: "Where to Buy",
    // Where-to-Buy landing arrives in Phase 1B — for now route to Contact
    // (WhatsApp intent + county input handled by the technical team).
    to: "/contact",
    testid: "nav-where-to-buy",
  },
  {
    label: "Partner",
    to: "/become-a-stockist",
    testid: "nav-partner",
    submenu: [
      { label: "Stockist / Agrovet", to: "/become-a-stockist", testid: "nav-partner-stockist" },
      { label: "Distributor / Institution", to: "/distribution", testid: "nav-partner-distribution" },
    ],
  },
  { label: "About", to: "/about", testid: "nav-about" },
];

const CONTACT_WA = buildWaLink(WA_TEMPLATES.technical_general + " ");

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-bah-line"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        {/* Brand — logo also acts as Home */}
        <Link
          to="/"
          data-testid="header-logo-link"
          aria-label="Biodeal Animal Health — Home"
          className="flex items-center gap-3 min-w-0"
        >
          <span className="block md:hidden">
            <BiodealLogo size="sm" />
          </span>
          <span className="hidden md:block">
            <BiodealLogo size="md" />
          </span>
          <div className="leading-tight min-w-0">
            <div className="font-display text-[17px] sm:text-[19px] md:text-[21px] font-semibold text-bah-ink tracking-tight truncate">
              Biodeal Animal Health
            </div>
            <div className="hidden md:block font-sans text-[10px] uppercase tracking-[0.22em] text-bah-green">
              Responsible · Trusted · Built for Farmers
            </div>
          </div>
        </Link>

        {/* Primary nav — visible from the fit breakpoint (see tailwind.config.js) */}
        <nav
          className="hidden nav-fit:flex items-center gap-0.5"
          aria-label="Primary"
          data-testid="primary-nav"
        >
          {NAV.map((item) =>
            item.submenu ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  type="button"
                  data-testid={item.testid}
                  className="px-3 py-2 text-sm font-medium text-bah-ink hover:text-bah-green inline-flex items-center gap-1"
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {openMenu === item.label && (
                  <div className="absolute top-full left-0 pt-1 min-w-[240px] z-50">
                    <div className="bg-white border border-bah-line rounded-xl shadow-xl p-2">
                      {item.submenu.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          data-testid={sub.testid}
                          className={({ isActive }) =>
                            `block px-3 py-2 text-sm rounded-lg ${
                              isActive
                                ? "bg-bah-green/10 text-bah-green"
                                : "text-bah-ink hover:bg-bah-paperAlt"
                            }`
                          }
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-bah-green" : "text-bah-ink hover:text-bah-green"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Right cluster — Contact utility (desktop) + hamburger toggle (mobile/tablet) */}
        <div className="flex items-center gap-2">
          <a
            href={CONTACT_WA}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-contact-cta"
            aria-label="Contact Biodeal Animal Health on WhatsApp"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-bah-ink text-white text-xs md:text-sm font-semibold px-3 md:px-4 py-2 md:py-2.5 hover:bg-bah-ink/90 transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
            <span className="hidden md:inline">Contact / WhatsApp</span>
            <span className="md:hidden">Contact</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="nav-fit:hidden p-2 rounded-lg border border-bah-line text-bah-ink"
            data-testid="mobile-menu-toggle"
            aria-label="Open navigation menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile / tablet drawer */}
      {open && (
        <div className="nav-fit:hidden border-t border-bah-line bg-white" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <div key={item.label}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-${item.testid}`}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-lg text-base font-medium ${
                      isActive
                        ? "bg-bah-green/10 text-bah-green"
                        : "text-bah-ink hover:bg-bah-paperAlt"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
                {item.submenu && (
                  <div className="pl-4">
                    {item.submenu.map((sub) => (
                      <NavLink
                        key={sub.to}
                        to={sub.to}
                        data-testid={`mobile-${sub.testid}`}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-lg text-sm ${
                            isActive ? "text-bah-green" : "text-bah-mute hover:text-bah-green"
                          }`
                        }
                      >
                        — {sub.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={CONTACT_WA}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-header-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-bah-ink text-white text-sm font-semibold px-4 py-3 mt-3"
            >
              <MessageCircle className="h-4 w-4" /> Contact / WhatsApp
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
